'use client'

import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useRef, useState } from 'react'
import { useProperty, useUpdateProperty } from '@/hooks/use-properties'
import { PropertyForm } from '@/components/partials/properties'
import { type PropertyFormRef } from '@/components/partials/properties/form'
import { FormSkeleton, ContentError } from '@/components/partials'
import { UpdatePropertyParams } from '@/types/property'
import { FormModeEnum } from '@/enums'

interface UpdateProps {
  id: string
}

const Update = ({ id }: UpdateProps) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const propertyFormRef = useRef<PropertyFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: property, isLoading, error } = useProperty(id)
  const { mutate: updateProperty, isPending } = useUpdateProperty()

  const handleSubmit = (data: UpdatePropertyParams) => {
    updateProperty(
      { propertyId: id, params: data },
      {
        onSuccess: async updatedProperty => {
          setIsRedirecting(true)
          queryClient.setQueryData(['property', id], updatedProperty)
          await queryClient.invalidateQueries({ queryKey: ['properties'] })
          toast.success(t('updated_successfully'))
          router.push(`/products/properties/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (propertyFormRef.current) {
        propertyFormRef.current.handleValidationErrors(error)
      }
      return
    }

    toast.error(t('updating_message'))
  }

  const handleCancel = () => {
    router.push(`/products/properties/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={1} />
  }

  if (error || !property) {
    return <ContentError error={error} listUrl="/products/properties" />
  }

  return (
    <PropertyForm
      ref={propertyFormRef}
      mode={FormModeEnum.UPDATE}
      property={property}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
